import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, GripVertical, Pencil, Trash2, KeyRound } from "lucide-react";
import LogoDisplay from "@/components/LogoDisplay";
import LinkFormDialog from "./LinkFormDialog";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";

function SortableRow({ link, categoryName, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: link.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="border-b border-border last:border-0"
      data-testid={`link-row-${link.id}`}
    >
      <td className="p-3 w-10">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-primary px-1 py-2 rounded-sm hover:bg-accent transition-colors"
          aria-label="Glisser pour réordonner"
          title="Glissez pour réordonner"
          data-testid={`link-drag-${link.id}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </td>
      <td className="p-3">
        <LogoDisplay type={link.logo_type} value={link.logo_value} size={40} />
      </td>
      <td className="p-3">
        <div className="font-medium text-sm flex items-center gap-1.5">
          {link.title}
          {link.has_credentials && (
            <KeyRound
              className="h-3 w-3 text-amber-600 dark:text-amber-400"
              title="Identifiants enregistrés (copiés au clic dans le portail)"
              data-testid={`link-has-creds-${link.id}`}
            />
          )}
        </div>
        <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground hover:underline">
          {link.url}
        </a>
      </td>
      <td className="p-3 text-sm text-muted-foreground hidden md:table-cell">
        {categoryName || "—"}
      </td>
      <td className="p-3 text-sm tabular-nums hidden sm:table-cell">{link.clicks}</td>
      <td className="p-3 text-right">
        <div className="inline-flex items-center gap-1">
          <button
            onClick={() => onEdit(link)}
            className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-border hover:bg-accent"
            aria-label="Éditer"
            data-testid={`link-edit-${link.id}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onDelete(link)}
            className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-border hover:bg-destructive hover:text-destructive-foreground hover:border-destructive"
            aria-label="Supprimer"
            data-testid={`link-delete-${link.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function LinksManager() {
  const [links, setLinks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterCat, setFilterCat] = useState("all");

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const filteredLinks = filterCat === "all"
    ? links
    : links.filter((l) => (l.category_id || "") === filterCat);

  const load = async () => {
    setLoading(true);
    try {
      const [linksRes, catsRes] = await Promise.all([
        api.get("/admin/links"),
        api.get("/admin/categories"),
      ]);
      setLinks(linksRes.data);
      setCategories(catsRes.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = links.findIndex((l) => l.id === active.id);
    const newIndex = links.findIndex((l) => l.id === over.id);
    const next = arrayMove(links, oldIndex, newIndex);
    setLinks(next);
    try {
      await api.post("/admin/links/reorder", { ids: next.map((l) => l.id) });
      toast.success("Ordre des liens mis à jour");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const handleDelete = async (link) => {
    if (!window.confirm(`Supprimer le lien « ${link.title} » ?`)) return;
    try {
      await api.delete(`/admin/links/${link.id}`);
      toast.success("Lien supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name;

  return (
    <div className="space-y-6" data-testid="admin-links-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
            // Gestion
          </p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Liens</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Glissez la poignée <GripVertical className="inline h-3.5 w-3.5 text-primary" /> à gauche de chaque ligne pour réordonner.
            Filtrez par catégorie pour réordonner uniquement dans celle-ci.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)}
            className="h-10 px-3 rounded-sm border border-border bg-background text-sm"
            data-testid="links-category-filter"
          >
            <option value="all">Toutes les catégories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.parent_id ? "↳ " : ""}{c.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
            className="h-10 px-4 inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium"
            data-testid="add-link-button"
          >
            <Plus className="h-4 w-4" />
            Ajouter un lien
          </button>
        </div>
      </div>

      {error && <p className="text-destructive text-sm">{error}</p>}

      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        {loading ? (
          <p className="p-6 text-sm text-muted-foreground">Chargement…</p>
        ) : links.length === 0 ? (
          <p className="p-12 text-center text-sm text-muted-foreground" data-testid="links-empty">
            Aucun lien. Cliquez sur « Ajouter un lien » pour commencer.
          </p>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-border bg-accent/30 text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="p-3 w-10"></th>
                  <th className="p-3">Logo</th>
                  <th className="p-3">Lien</th>
                  <th className="p-3 hidden md:table-cell">Catégorie</th>
                  <th className="p-3 hidden sm:table-cell">Clics</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <SortableContext items={filteredLinks.map((l) => l.id)} strategy={verticalListSortingStrategy}>
                <tbody>
                  {filteredLinks.length === 0 ? (
                    <tr><td colSpan={6} className="p-12 text-center text-sm text-muted-foreground">Aucun lien dans cette catégorie.</td></tr>
                  ) : filteredLinks.map((l) => (
                    <SortableRow
                      key={l.id}
                      link={l}
                      categoryName={catName(l.category_id)}
                      onEdit={(link) => {
                        setEditing(link);
                        setDialogOpen(true);
                      }}
                      onDelete={handleDelete}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </table>
          </DndContext>
        )}
      </div>

      <LinkFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        link={editing}
        categories={categories}
        onSaved={load}
      />
    </div>
  );
}
